import { useMutation, useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { getLocationPoints, scanLocation } from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import { scanPointToWorldMap } from "@/utils/worldMapPoints"
import {
  canActivateWorldMapPoint,
  listNearestWorldMapPoints,
} from "@/utils/worldMapProximity"
import { watchPlayerGps, PlayerGps } from "@/utils/playerGps"
import { useEffect, useState } from "react"
import { distanceMeters } from "@/utils/geo"

function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} м`
  return `${(m / 1000).toFixed(1)} км`
}

export default function LocationScanPage() {
  const [params] = useSearchParams()
  const highlightId = params.get("pointId")
  const [gps, setGps] = useState<PlayerGps | null>(null)
  const { syncGameState, bumpCards } = useGuardiansPassport()

  useEffect(() => watchPlayerGps(setGps), [])

  const locQuery = useQuery({
    queryKey: ["location-points"],
    queryFn: getLocationPoints,
  })

  const scanPoints = useMemo(() => {
    const raw = locQuery.data?.points ?? []
    return raw
      .filter((p) => !p.mapKind || p.mapKind === "scan" || p.mapKind === "treasure")
      .map(scanPointToWorldMap)
  }, [locQuery.data?.points])

  const foreignScans = locQuery.data?.foreignScans ?? null

  const nearby = useMemo(
    () => listNearestWorldMapPoints(scanPoints, gps, foreignScans, 12),
    [scanPoints, gps, foreignScans]
  )

  const highlight = useMemo(() => {
    if (!highlightId || !gps) return null
    const wm = scanPoints.find(
      (p) => p.id === highlightId || p.sourceId === highlightId
    )
    if (!wm) return null
    return {
      point: wm,
      distanceM: distanceMeters(gps.lat, gps.lng, wm.lat, wm.lng),
      actionable: canActivateWorldMapPoint(wm, gps, foreignScans),
    }
  }, [highlightId, gps, scanPoints, foreignScans])

  const scan = useMutation({
    mutationFn: (pointId: string) => {
      if (!gps) throw new Error("Нужен GPS")
      return scanLocation({
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void locQuery.refetch()
    },
  })

  const lastResult = scan.data

  return (
    <GameScreenLayout title="Скан локации" backTo="/play/map">
      {!gps ? (
        <p className="mb-3 text-sm text-amber-200/90">
          Разрешите геолокацию в браузере, чтобы сканировать точки.
        </p>
      ) : null}

      {foreignScans ? (
        <p className="mb-3 text-xs text-guardians-muted">
          Сканы в чужих городах: {foreignScans.remaining}/{foreignScans.limit}
        </p>
      ) : null}

      {highlight ? (
        <section className="mb-4 rounded-xl border border-guardians-accent/40 bg-guardians-accent/10 p-3">
          <p className="text-sm font-medium">{highlight.point.title}</p>
          <p className="text-xs text-guardians-muted">
            {formatDistance(highlight.distanceM)}
            {highlight.actionable ? " · в радиусе" : " · подойдите ближе"}
          </p>
          <ScanButton
            disabled={!highlight.actionable || scan.isPending}
            onScan={() => scan.mutate(highlight.point.sourceId)}
          />
        </section>
      ) : null}

      {scan.isError ? (
        <p className="mb-3 text-sm text-red-300">
          {getGameApiErrorMessage(scan.error, "Скан не удался")}
        </p>
      ) : null}

      {lastResult ? (
        <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm">
          {lastResult.message ? <p>{lastResult.message}</p> : null}
          {lastResult.lootType ? (
            <p>
              Награда: {lastResult.lootType}{" "}
              {lastResult.amount != null ? `× ${lastResult.amount}` : ""}
            </p>
          ) : null}
          {lastResult.card ? (
            <p>Карта: {lastResult.card.element} Lv.{lastResult.card.level}</p>
          ) : null}
        </div>
      ) : null}

      {locQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка точек…</p>
      ) : (
        <ul className="space-y-2">
          {nearby.map(({ point, distanceM, actionable }) => (
            <li
              key={point.id}
              className={clsx(
                "flex items-center justify-between gap-2 rounded-xl border p-3",
                point.sourceId === highlightId || point.id === highlightId
                  ? "border-guardians-accent/50"
                  : "border-white/10"
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{point.title}</p>
                <p className="text-xs text-guardians-muted">
                  {formatDistance(distanceM)}
                  {actionable ? "" : " · далеко или недоступно"}
                </p>
              </div>
              <ScanButton
                disabled={!actionable || !gps || scan.isPending}
                onScan={() => scan.mutate(point.sourceId)}
              />
            </li>
          ))}
        </ul>
      )}
    </GameScreenLayout>
  )
}

function ScanButton({
  disabled,
  onScan,
}: {
  disabled: boolean
  onScan: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onScan}
      className="shrink-0 rounded-lg bg-guardians-accent px-3 py-1.5 text-xs font-medium text-guardians-bg disabled:opacity-40"
    >
      Скан
    </button>
  )
}
